import { Portal, PortalHost } from '@gorhom/portal';
import React, { useEffect, useRef } from 'react';
import { Animated, Easing, Platform, Pressable, View } from 'react-native';
import { colors, zIndex, shadows } from '../../../utils/theme';
import { useOnLayoutChange } from '../LayoutChangeEventProvider';

export type SideDrawerContainerContext = {
    closeDrawer: (contentKey: string) => void;
    openDrawer: (contentKey: string) => void;
    getShouldMountContent: (contentKey: string) => boolean;
    forceCloseDrawer: (contentKey: string) => void;
    isSideDrawerOpen: boolean;
    /**
     * If true, the user is trying to close the drawer but the child component requested that the user confirm the action.
     */
    isRequestingCloseConfirmation: boolean;
    /**
     * Set to true if the child component wants to request that the user confirm the action of closing the drawer.
     */
    setShouldRequestCloseConfirmation: (value: boolean) => void;
    isInSideDrawerContainer: boolean;
};

export const SideDrawerContainerContext = React.createContext<SideDrawerContainerContext>({
    closeDrawer: () => {},
    openDrawer: () => {},
    getShouldMountContent: () => false,
    forceCloseDrawer: () => {},
    isSideDrawerOpen: false,
    isRequestingCloseConfirmation: false,
    setShouldRequestCloseConfirmation: () => {},
    isInSideDrawerContainer: false,
});

export type SideDrawerContainerOwnProps = {
    children: React.ReactNode;
};

export type SideDrawerContainerProps = SideDrawerContainerOwnProps;

const sideDrawerDetailsPortal = 'side-drawer-details-portal';

const drawerWidth = 400;

export const SIDE_DRAWER_ANIMATION_DURATION = 300;

const SideDrawerContainer: React.FC<SideDrawerContainerProps> = (props) => {
    const [shouldRequestCloseConfirmation, setShouldRequestCloseConfirmation] = React.useState(false);
    const [isPendingCloseConfirmation, setIsPendingCloseConfirmation] = React.useState(false);
    const [openDrawerContentKey, setOpenDrawerContentKey] = React.useState<string | null>(null);
    const [isOpen, setIsOpen] = React.useState(false);
    const [isAnimating, setIsAnimating] = React.useState(false);
    const presenceAnimationRef = useRef(new Animated.Value(0));
    const animationRunIdRef = useRef(0);
    const [shouldMountContent, setShouldMountContent] = React.useState(false);
    const onLayoutChange = useOnLayoutChange();
    useEffect(() => {
        const runId = ++animationRunIdRef.current;
        setIsAnimating(true);
        if (isOpen) {
            setShouldMountContent(true);
        }

        Animated.timing(presenceAnimationRef.current, {
            toValue: isOpen ? 1 : 0,
            duration: SIDE_DRAWER_ANIMATION_DURATION,
            useNativeDriver: Platform.OS === 'web',
            easing: Easing.inOut(Easing.ease),
        }).start((result) => {
            // Only the newest run may touch the flag: an interrupted run reports finished: false
            // after its replacement has already re-armed it.
            if (animationRunIdRef.current !== runId) return;
            if (result.finished) {
                onLayoutChange();
                if (!isOpen) {
                    setShouldMountContent(false);
                }
                if (openDrawerContentKey) {
                    setOpenDrawerContentKey(isOpen ? openDrawerContentKey : null);
                }
            }
            // Cleared even when interrupted, so the drawer never strands `animating-parent` on the
            // page content it wraps.
            setIsAnimating(false);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const closeDrawer = React.useCallback(
        (contentKey: string) => {
            if (contentKey === openDrawerContentKey) {
                if (shouldRequestCloseConfirmation) {
                    setIsPendingCloseConfirmation(true);
                    setShouldRequestCloseConfirmation(false);
                } else {
                    setIsOpen(false);
                }
            }
        },
        [openDrawerContentKey, shouldRequestCloseConfirmation],
    );

    const openDrawer = React.useCallback((contentKey: string) => {
        setIsOpen(true);
        setOpenDrawerContentKey(contentKey);
        setShouldRequestCloseConfirmation(false);
        setIsPendingCloseConfirmation(false);
    }, []);

    const forceCloseDrawer = React.useCallback(
        (contentKey: string) => {
            if (contentKey === openDrawerContentKey) {
                setIsOpen(false);
                setShouldMountContent(false);
                setShouldRequestCloseConfirmation(false);
                setIsPendingCloseConfirmation(false);
            }
        },
        [openDrawerContentKey],
    );

    const getShouldMountContent = React.useCallback(
        (contentKey: string) => {
            return contentKey === openDrawerContentKey;
        },
        [openDrawerContentKey],
    );

    return (
        <SideDrawerContainerContext.Provider
            value={{
                closeDrawer,
                openDrawer,
                getShouldMountContent,
                forceCloseDrawer,
                isSideDrawerOpen: isOpen,
                isRequestingCloseConfirmation: isPendingCloseConfirmation,
                setShouldRequestCloseConfirmation: (value) => {
                    setShouldRequestCloseConfirmation(value);
                    setIsPendingCloseConfirmation(false);
                },
                isInSideDrawerContainer: true,
            }}
        >
            <View
                style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden' }}
                testID={isAnimating ? 'animating-parent' : undefined}
            >
                <View
                    style={{
                        height: '100%',
                        width: '100%',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                    }}
                >
                    {props.children}
                </View>
                {shouldMountContent ? (
                    <Animated.View
                        style={{
                            height: '100%',
                            width: '100%',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            backgroundColor: colors.Black,
                            opacity: presenceAnimationRef.current.interpolate({
                                inputRange: [0, 1],
                                outputRange: [0, 0.5],
                            }),
                        }}
                    >
                        <Pressable
                            style={{ width: '100%', height: '100%' }}
                            onPress={() => {
                                if (openDrawerContentKey) closeDrawer(openDrawerContentKey);
                            }}
                        />
                    </Animated.View>
                ) : null}
                {shouldMountContent ? (
                    <Animated.View
                        style={{
                            height: '100%',
                            width: drawerWidth,
                            position: 'absolute',
                            right: presenceAnimationRef.current.interpolate({
                                inputRange: [0, 1],
                                outputRange: [-drawerWidth, 0],
                            }),
                            opacity: presenceAnimationRef.current.interpolate({
                                inputRange: [0, 1],
                                outputRange: [0, 1],
                            }),
                            top: 0,
                            bottom: 0,
                            zIndex: zIndex.drawer,
                            backgroundColor: colors.White,
                            ...shadows.default,
                        }}
                    >
                        <PortalHost name={sideDrawerDetailsPortal} />
                    </Animated.View>
                ) : null}
            </View>
        </SideDrawerContainerContext.Provider>
    );
};

export default React.memo(SideDrawerContainer);

export const SideDrawerContent: React.FC<{ children: any }> = ({ children }) => {
    return <Portal hostName={sideDrawerDetailsPortal}>{children}</Portal>;
};

export const useIsInSideDrawerContainer = () => {
    return React.useContext(SideDrawerContainerContext).isInSideDrawerContainer;
};
